import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { TaskType, TaskTypes } from '../../../../../@types/task.type';
import { TaskSubmitInput } from '../../useServiceDialogs';
import { TaskDurationInput } from './TaskDurationInput';

export interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  serviceId: number;
  taskTypes: TaskTypes[];
  taskTypesLoading: boolean;
  onSubmit: (task: TaskSubmitInput) => void;
  submitting: boolean;
}

const TIME_TASKS = new Set([TaskType.LABOUR_TIME, TaskType.TRAVEL_TIME, TaskType.STAGING_TIME, TaskType.LOADING_TIME]);
const QUANTITY_TASKS = new Set([TaskType.TRAVEL_DISTANCE, TaskType.RESOURCE_UTILISED]);

export const TaskDialog: React.FC<TaskDialogProps> = ({
  open,
  onClose,
  serviceId,
  taskTypes,
  taskTypesLoading,
  onSubmit,
  submitting,
}) => {
  const [selectedTypeCode, setSelectedTypeCode] = useState('');
  const [duration, setDuration] = useState('00:00');
  const [quantity, setQuantity] = useState<number | ''>('');
  const [quoteValue, setQuoteValue] = useState<number | ''>('');
  const [issuedTo, setIssuedTo] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedTypeCode('');
      setDuration('00:00');
      setQuantity('');
      setQuoteValue('');
      setIssuedTo('');
      setContactEmail('');
      setNotes('');
    }
  }, [open]);

  const selectedType = taskTypes.find((t) => t.code === selectedTypeCode);
  const isTimeTask = TIME_TASKS.has(selectedTypeCode as TaskType);
  const isQuantityTask = QUANTITY_TASKS.has(selectedTypeCode as TaskType);
  const isQuoteTask = selectedTypeCode === TaskType.QUOTE_VALUE;

  const buildPayload = (execute: boolean): TaskSubmitInput => {
    const taskData: Record<string, unknown> = { notes };
    if (isTimeTask) taskData.duration = duration;
    if (isQuantityTask) taskData.quantity = quantity;
    if (isQuoteTask) {
      taskData.valueCollection = quoteValue;
      taskData.issuedTo = issuedTo;
      taskData.contactEmail = contactEmail;
      taskData.isApproved = false;
    }
    return {
      taskTypeCode: selectedTypeCode,
      taskTypeCategory: selectedType?.category,
      serviceId,
      taskData,
      execute,
    };
  };

  const canSubmit = selectedTypeCode && !submitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Add Task</DialogTitle>
      <DialogContent>
        {taskTypesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select size="small" label="Task Type" value={selectedTypeCode}
              onChange={(e) => setSelectedTypeCode(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
            >
              {taskTypes.filter((t) => t.isActive).map((t) => (
                <MenuItem key={t.code} value={t.code} sx={{ fontSize: '12px' }}>{t.name}</MenuItem>
              ))}
            </TextField>

            {isTimeTask && (
              <TaskDurationInput value={duration} onChange={setDuration} />
            )}

            {isQuantityTask && (
              <TextField
                size="small" type="number" label={selectedTypeCode === TaskType.TRAVEL_DISTANCE ? 'Distance (KM)' : 'Quantity'}
                value={quantity} onChange={(e) => setQuantity(e.target.value ? Number(e.target.value) : '')}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
              />
            )}

            {isQuoteTask && (
              <>
                <TextField
                  size="small" type="number" label="Quote Value"
                  value={quoteValue} onChange={(e) => setQuoteValue(e.target.value ? Number(e.target.value) : '')}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
                />
                <TextField
                  size="small" label="Issued To" value={issuedTo}
                  onChange={(e) => setIssuedTo(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
                />
                <TextField
                  size="small" label="Contact Email" value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
                />
              </>
            )}

            {selectedTypeCode && (
              <TextField
                size="small" label="Notes" multiline minRows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="outlined"
          onClick={() => onSubmit(buildPayload(false))}
          disabled={!canSubmit}
          sx={{ textTransform: 'none' }}
        >
          Save
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(buildPayload(true))}
          disabled={!canSubmit}
          sx={{ textTransform: 'none', bgcolor: 'v4.accent', '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 } }}
        >
          {submitting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : 'Execute'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
